// Public Portal Home Page
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  CardActionArea,
  Grid,
  Button,
  Chip,
  Stack,
} from '@mui/material';
import {
  Article,
  Lightbulb,
  BugReport,
  HelpOutline,
  Email,
  ChevronRight,
  ThumbUp,
} from '@mui/icons-material';
import { usePortal } from './PortalLayout';
import { articlesService } from '@/services/collections/articles';
import { featuresService } from '@/services/collections/features';
import type { Article as ArticleType, FeatureRequest } from '@/types';

export const PortalHomePage = () => {
  const navigate = useNavigate();
  const { organization, project } = usePortal();

  const [popularArticles, setPopularArticles] = useState<ArticleType[]>([]);
  const [topFeatures, setTopFeatures] = useState<FeatureRequest[]>([]);

  useEffect(() => {
    const fetchData = async () => {
      if (!organization || !project) return;

      try {
        // Fetch popular articles
        if (project.features.knowledgeBaseEnabled) {
          const articles = await articlesService.getPopular(organization.id, project.id, 4);
          setPopularArticles(articles);
        }

        // Fetch top voted features
        if (project.features.featureRequestsEnabled) {
          const features = await featuresService.getTopVoted(organization.id, project.id, 3);
          setTopFeatures(features);
        }
      } catch (error) {
        console.error('Error fetching portal data:', error);
      }
    };

    fetchData();
  }, [organization, project]);

  const quickActions = [
    {
      icon: <BugReport sx={{ fontSize: 40 }} />,
      title: 'Report a Bug',
      description: 'Found something broken? Let us know.',
      path: `submit?type=bug`,
      color: '#ef4444',
    },
    {
      icon: <HelpOutline sx={{ fontSize: 40 }} />,
      title: 'Get Help',
      description: 'Need assistance? Submit a support request.',
      path: `submit?type=support`,
      color: '#3b82f6',
    },
    {
      icon: <Lightbulb sx={{ fontSize: 40 }} />,
      title: 'Suggest a Feature',
      description: 'Have an idea? We\'d love to hear it.',
      path: `submit?type=feature`,
      color: '#f59e0b',
    },
    {
      icon: <Email sx={{ fontSize: 40 }} />,
      title: 'Send Feedback',
      description: 'Share your thoughts with our team.',
      path: `submit?type=feedback`,
      color: '#10b981',
    },
  ];

  return (
    <Box>
      {/* Quick Actions */}
      <Typography variant="h5" fontWeight="bold" gutterBottom>
        How can we help?
      </Typography>
      <Grid container spacing={2} sx={{ mb: 6 }}>
        {quickActions.map((action) => (
          <Grid size={{ xs: 12, sm: 6, md: 3 }} key={action.title}>
            <Card
              sx={{
                height: '100%',
                transition: 'transform 0.2s, box-shadow 0.2s',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: 4,
                },
              }}
            >
              <CardActionArea
                onClick={() => navigate(action.path)}
                sx={{ height: '100%', p: 2 }}
              >
                <Box sx={{ color: action.color, mb: 1 }}>{action.icon}</Box>
                <Typography variant="h6" fontWeight="bold" gutterBottom>
                  {action.title}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {action.description}
                </Typography>
              </CardActionArea>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Popular Articles */}
      {project?.features.knowledgeBaseEnabled && popularArticles.length > 0 && (
        <Box sx={{ mb: 6 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h5" fontWeight="bold">
              Popular Articles
            </Typography>
            <Button
              endIcon={<ChevronRight />}
              onClick={() => navigate('kb')}
            >
              View All
            </Button>
          </Box>
          <Grid container spacing={2}>
            {popularArticles.map((article) => (
              <Grid size={{ xs: 12, sm: 6 }} key={article.id}>
                <Card
                  sx={{
                    '&:hover': { boxShadow: 2 },
                    cursor: 'pointer',
                  }}
                  onClick={() => navigate(`kb/${article.slug}`)}
                >
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                      <Article color="primary" />
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="subtitle1" fontWeight="medium">
                          {article.title}
                        </Typography>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                          }}
                        >
                          {article.excerpt || article.content.slice(0, 120)}
                        </Typography>
                        {article.category && (
                          <Chip label={article.category} size="small" sx={{ mt: 1 }} />
                        )}
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>
      )}

      {/* Top Feature Requests */}
      {project?.features.featureRequestsEnabled && topFeatures.length > 0 && (
        <Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h5" fontWeight="bold">
              Popular Feature Requests
            </Typography>
            <Button
              endIcon={<ChevronRight />}
              onClick={() => navigate('ideas')}
            >
              View All
            </Button>
          </Box>
          <Stack spacing={2}>
            {topFeatures.map((feature) => (
              <Card
                key={feature.id}
                sx={{ '&:hover': { boxShadow: 2 }, cursor: 'pointer' }}
                onClick={() => navigate(`ideas/${feature.id}`)}
              >
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        minWidth: 50,
                      }}
                    >
                      <ThumbUp color="primary" />
                      <Typography variant="h6" fontWeight="bold">
                        {feature.voteCount}
                      </Typography>
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="subtitle1" fontWeight="medium">
                        {feature.title}
                      </Typography>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                        }}
                      >
                        {feature.description}
                      </Typography>
                    </Box>
                    <Chip
                      label={feature.status.replace('_', ' ')}
                      size="small"
                      color={
                        feature.status === 'planned'
                          ? 'info'
                          : feature.status === 'in_progress'
                          ? 'warning'
                          : feature.status === 'released'
                          ? 'success'
                          : 'default'
                      }
                    />
                  </Box>
                </CardContent>
              </Card>
            ))}
          </Stack>
        </Box>
      )}
    </Box>
  );
};
